import React, { useEffect, useRef } from 'react';
import { useVideoCall } from '../../context/VideoCallContext';

/**
 * Plays the remote party's audio for the whole call through one hidden <audio>
 * element that stays mounted, independent of the call widget's layout.
 *
 * Previously audio came from the remote <video> element, which is swapped out
 * whenever the widget is minimized/expanded, and which mobile browsers pause
 * when the screen locks or the app is backgrounded — nothing resumed it, so
 * sound "cut off after a while". The video elements are now muted (picture only)
 * and this element resumes itself whenever the browser pauses it.
 */
export const RemoteCallAudio: React.FC = () => {
  const { remoteStream, selectedAudioOutput } = useVideoCall();
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.srcObject !== remoteStream) el.srcObject = remoteStream;
    if (!remoteStream) return;

    const resume = () => {
      if (el.paused && el.srcObject) el.play().catch(() => { /* retried on next trigger */ });
    };
    const onVisible = () => { if (document.visibilityState === 'visible') resume(); };

    resume();
    el.addEventListener('pause', resume);         // paused by the browser/OS mid-call
    el.addEventListener('stalled', resume);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', resume);
    // A remote audio track that was muted by the network comes back via 'unmute'
    const tracks = remoteStream.getAudioTracks();
    tracks.forEach(t => t.addEventListener('unmute', resume));
    return () => {
      el.removeEventListener('pause', resume);
      el.removeEventListener('stalled', resume);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', resume);
      tracks.forEach(t => t.removeEventListener('unmute', resume));
    };
  }, [remoteStream]);

  // Speaker/headset selection (setSinkId is unsupported on Safari/iOS — no-op there)
  useEffect(() => {
    const el = audioRef.current as any;
    if (el && selectedAudioOutput && typeof el.setSinkId === 'function') {
      el.setSinkId(selectedAudioOutput).catch(() => {});
    }
  }, [selectedAudioOutput, remoteStream]);

  return <audio ref={audioRef} autoPlay style={{ display: 'none' }} />;
};
